"use client";

import type React from "react";
import { useMemo } from "react";
import type { PipelineCandidate, PipelineStage } from "@/lib/api/types";
import { CandidateAvatar } from "@/components/ui/candidate-avatar";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Icon } from "@/components/ui/icon";
import {
  SkeletonBox,
  SkeletonContainer,
  SkeletonLabel,
} from "@/components/ui/skeleton";
import { titleCasePreserveAcronyms } from "@/lib/string/title-case";
import { cn } from "@/lib/utils";
import { CompanyLogo } from "@/features/candidate";
import {
  PipelineSelectionProvider,
  usePipelineSelection,
} from "./table/selection-context";
import { usePipelineTableStore } from "@/features/jobs/store/pipeline/table";
import { usePipelineViewStore } from "@/features/jobs/store/pipeline/view";
import { ArTablePagination } from "@/components/ui/ar-table/pagination";

const PER_PAGE = 50;

interface MobilePipelineCardsProps {
  candidates: PipelineCandidate[];
  stages: PipelineStage[];
  totalCount: number;
  loading?: boolean;
  onCandidateClick: (candidate: PipelineCandidate) => void;
  onSelectAllCandidates?: () => void;
}

export function MobilePipelineCards({
  candidates,
  stages,
  totalCount,
  loading,
  onCandidateClick,
  onSelectAllCandidates,
}: MobilePipelineCardsProps) {
  const { page, setPage } = usePipelineTableStore();
  const { getSelectedCount } = usePipelineViewStore();
  const hasSelection = getSelectedCount() > 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / PER_PAGE));

  const stageMap = useMemo(() => {
    const map = new Map<string, PipelineStage>();
    for (const s of stages) map.set(s.id, s);
    return map;
  }, [stages]);

  // Twin of `EnrollmentMobileCards`' `showSkeleton`: only the true first-load
  // case (loading + nothing to show yet) swaps in the shimmer cards — a
  // background refetch (e.g. pagination) keeps `candidates` populated, so
  // real rows stay on screen instead of flashing back to placeholders.
  const showSkeleton = !!loading && candidates.length === 0;

  return (
    <PipelineSelectionProvider
      candidates={candidates}
      totalCount={totalCount}
      onCandidateClick={onCandidateClick}
      onSelectAllCandidates={onSelectAllCandidates}
    >
      <div className="flex flex-col flex-1 min-h-0">
        <div className={cn("flex-1 overflow-y-auto", hasSelection && "pb-28")}>
          {showSkeleton
            ? Array.from({ length: 6 }).map((_, index) => (
                <CandidateCardSkeleton key={index} />
              ))
            : candidates.map((candidate, index) => (
                <CandidateCard
                  key={candidate.job_candidate_id}
                  candidate={candidate}
                  index={index}
                  stageMap={stageMap}
                />
              ))}
        </div>

        <div className="flex items-center justify-between border-t border-border bg-surface-elevated px-4 h-10 shrink-0">
          <span className="text-sm font-medium text-contrast/60">
            {totalCount} {totalCount === 1 ? "profile" : "profiles"}
          </span>
          <ArTablePagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            disabled={loading}
          />
        </div>
      </div>
    </PipelineSelectionProvider>
  );
}

/** Shimmer stand-in for `CandidateCard`, same shape (checkbox, avatar + name,
 * job title, company, stage badge) so the loading state doesn't jump around
 * once real rows swap in — mirrors `EnrollmentCardSkeleton`. */
function CandidateCardSkeleton() {
  return (
    <SkeletonContainer className="flex items-start gap-3 border-b border-border px-4 py-3.5">
      <SkeletonBox className="mt-1 size-4 shrink-0 rounded-sm" />
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <div className="flex items-center gap-2">
          <SkeletonBox className="size-[26px] shrink-0 rounded-full" />
          <SkeletonLabel className="w-32" />
        </div>
        <SkeletonLabel className="w-24" />
        <div className="flex items-center gap-1.5">
          <SkeletonBox className="size-4 shrink-0 rounded-sm" />
          <SkeletonLabel className="w-28" />
        </div>
        <SkeletonBox className="h-5 w-16 rounded-md" />
      </div>
    </SkeletonContainer>
  );
}

function CandidateCard({
  candidate,
  index,
  stageMap,
}: {
  candidate: PipelineCandidate;
  index: number;
  stageMap: Map<string, PipelineStage>;
}) {
  const { isSelected, onCheckboxChange, onRowClick } = usePipelineSelection();
  const selected = isSelected(candidate.job_candidate_id);
  const displayName = candidate.full_name ?? "Unknown";
  const stage = stageMap.get(candidate.stage_id ?? "");
  const isImport = !!candidate.origin && candidate.origin.startsWith("upload_");

  return (
    <div
      className={cn(
        "flex items-start gap-3 border-b border-border px-4 py-3.5 transition-colors",
        selected ? "bg-surface-primary" : "bg-surface-primary active:bg-surface-inset",
      )}
      onClick={(e) => onRowClick(candidate, index, e)}
    >
      <div className="mt-1 shrink-0">
        <Checkbox
          checked={selected}
          onClick={(e) => {
            // Handler sits directly on the Checkbox (desktop `SelectCell`
            // pattern). base-ui renders a `role="checkbox"` control that
            // re-entrantly clicks a hidden input on tap; wiring selection to a
            // wrapper `onClick` instead double-fired `onCheckboxChange`
            // (two toggles = no net selection). `preventDefault` matches the
            // desktop cell; `stopPropagation` keeps the row's own `onClick`
            // (open/navigate) from firing on a checkbox tap.
            e.preventDefault();
            e.stopPropagation();
            onCheckboxChange(candidate.job_candidate_id, index, e);
          }}
        />
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <div className="flex items-center gap-2">
          <CandidateAvatar
            name={displayName}
            avatarUrl={candidate.avatar_url}
            size={26}
            initialsPx={10}
          />
          <span className="min-w-0 truncate text-sm font-semibold text-heading">
            {displayName}
          </span>
          {candidate.origin === "search" && (
            <Badge
              variant="linear"
              size="icon-sm"
              style={{ "--gradient-color": "var(--brand)" } as React.CSSProperties}
              className="shrink-0"
            >
              <Icon name="IconSearchIntelligence" size={11} className="text-brand" />
            </Badge>
          )}
          {isImport && (
            <div
              className="flex shrink-0 items-center justify-center size-5 rounded bg-[rgba(167,139,250,0.12)] border border-border"
              aria-label="Imported via Profile Import"
            >
              <Icon name="IconArrowOutOfBox" size={11} className="text-[#7c3aed] dark:text-[#a78bfa]" />
            </div>
          )}
        </div>

        {candidate.job_title && (
          <span className="text-xs text-contrast/70 truncate">
            {titleCasePreserveAcronyms(candidate.job_title)}
          </span>
        )}

        {candidate.company && (
          <div className="flex items-center gap-1.5">
            <CompanyLogo size="sm" domain={candidate.company_domain} name={candidate.company} />
            <span className="text-xs text-contrast/50 truncate">
              {titleCasePreserveAcronyms(candidate.company)}
            </span>
          </div>
        )}

        {stage && (
          <div>
            <Badge
              variant="linear"
              size="xs"
              style={{ "--gradient-color": stage.color ?? "#6b7280" } as React.CSSProperties}
            >
              {candidate.stage_name ?? stage.name}
            </Badge>
          </div>
        )}
      </div>
    </div>
  );
}
